import type { Dispatch, SetStateAction } from 'react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGuestSiteController } from './GuestSite.controller';
import { AnalyticsContext, useLocation } from '@shared/core';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated-base';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { StaticContext } from '@react-router';
import { Box, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { GuestIdentificationDialogProvider } from '@apps/guest/packages/layout-engine/components/GuestIdentificationDialog';
import { AutoUnlockGate } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AutoUnlockGate';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { SmartAppBanner, SmartAppBannerProvider, useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { WebsiteBannerProvider, useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { PremiumHeaderLayoutContext } from './hooks/usePremiumHeaderLayout';
import { HeaderLayoutUpgradeDialog } from '@shared/components/HeaderLayoutUpgradeDialog';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { GUEST_SITE_START_PORTAL_ID } from './GuestSite.constants';
import { GuestSiteHead } from './GuestSiteHead';

import { LazyStatusBanner } from './components/StatusBanner/StatusBanner.lazy';
import { LazyLayoutAloha } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.lazy';
import { LazyLayoutBrannan } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.lazy';
import { LazyLayoutBrannanSinglePage } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage.lazy';
import { LazyPreviewListener } from './PreviewListener.lazy';
import { TemplatePreviewProvider } from './TemplatePreviewContext';
import { AccommodationsLayoutOverrideProvider } from './AccommodationsLayoutOverrideContext';
import { LazyAnnouncement } from '@apps/guest/packages/layout-engine/components/Announcement/Announcement.lazy';
import { LazyPurchaseConfirmationDialog } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog.lazy';
import { RichTextStylesProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichTextStylesProvider';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';
import { PageContentVisibilityProvider } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { usePrefetchSsrQueries } from './hooks/usePrefetchSsrQueries';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { useFetchCustomPageData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchCustomPageData';
import { useFetchAccommodationsSsrData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchAccommodationsSsrData';

// Layout-level prefetches; runs above the `event && layout` gate so queries fire concurrently with EventWebsiteGuest.
const PrefetchSsrQueriesRenderer: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  usePrefetchSsrQueries({ eventHandle });
  return null;
};

// Conditional mount keeps the GuestEventHotelRoomBlocks observer off non-redesign events —
// controller's `refetchQueries({ include: refetchPageQueries.accommodations })` matches by
// operation name and would bypass the hook's internal `skip`.
const HoneymoonAccommodationsPrefetchInner: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const { data: pageContainerData } = useFetchCustomPageData(eventHandle, false);
  useFetchAccommodationsSsrData({ eventId, pageContainerData });
  return null;
};

const HoneymoonAccommodationsPrefetchRenderer: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const isStaysRedesignActive = useIsStaysRedesignActive();
  if (!isStaysRedesignActive) return null;
  return (
    <HoneymoonAccommodationsPrefetchInner
      eventHandle={eventHandle}
      eventId={eventId}
    />
  );
};

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    setIsPastFirstPage?: (value: boolean) => void;
    isNativeAccommodationPage: boolean;
    isNativeBrannanOverridePage: boolean;
  }> {}

const GuestSiteLayout: React.FC<GuestSiteLayoutProps> = ({
  eventHandle,
  event,
  layout,
  setLoadingTheme,
  setIsLastPageVisible,
  setIsPastFirstPage,
  isNativeBrannanOverridePage,
}) => {
  const brannanNavigation = event.eventDesign?.brannanNavigation;
  const layoutSwitch = () => {
    const layoutTypes = {
      [DesignLayoutType.aloha]: LazyLayoutAloha,
      [DesignLayoutType.brannan]: !isNativeBrannanOverridePage && brannanNavigation === BrannanNavigation.singlePage ? LazyLayoutBrannanSinglePage : LazyLayoutBrannan,
    };
    const Layout = layoutTypes[layout];

    return (
      (event && (
        <Layout
          setLoadingTheme={setLoadingTheme}
          setIsLastPageVisible={setIsLastPageVisible}
          setIsPastFirstPage={setIsPastFirstPage}
          eventHandle={eventHandle}
          event={event}
        />
      )) ||
      null
    );
  };

  return layoutSwitch();
};

export interface GuestSiteBodyProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBodyBase: React.FC<GuestSiteBodyProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsPastFirstPage,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeAccommodationPage,
    isNativeBrannanOverridePage,
    showUnpublishedPage,
    premiumHeaderLayout,
    loadingGuestSite,
    isLoadingWebsitePublishCapability,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();
  const isFullySsrEnabled = useIsGuestSiteSsrEnabled();

  usePreloadTranslationNamespaces(
    'joykit',
    'guestKit',
    'layoutEngine',
    'headerLayoutUpgrade',
    'sharedComponents',
    'countries',
    'guestSiteCustom',
    'travelMap',
    'accommodationsGuest',
    'hotelTile',
    'guestSiteTravel',
    'dates',
    'accommodationsConsole',
    'websitePages',
    'publishReminder',
    'eventSiteApps',
    'guestRegistry',
    'guestUnlock',
    'inlineEditor',
  );

  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const { pathname } = useLocation();
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  const guestSiteTelemetry = useGuestSiteTelemetry();

  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  const isTravelOrAccomPage = useMemo(() => pathname.includes('/travel') || pathname.includes('/accommodation'), [pathname]);
  const shouldRenderPurchaseConfirmationDialog = !!purchaseId && isTravelOrAccomPage;

  useEffect(() => {
    if (shouldRenderPurchaseConfirmationDialog) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: pathname.includes('/travel') ? 'travel' : 'accommodation' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderPurchaseConfirmationDialog]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  // Use website banner offset (no need for useBannersOffset anymore)
  const totalOffset = Math.max(smartAppBannerHeight, bannerOffset);

  // Stacked layouts (Aloha, Brannan single-page) defer non-target chrome so
  // the initial paint focuses on the deep-linked route. Brannan classic
  // short-circuits — renders one page per URL so there's nothing stacked to
  // hide, and a classic nav click otherwise gets trapped behind the initial-
  // slug ref until the fallback timer fires.
  //
  // `isNativeBrannanOverridePage` (e.g. native accommodations / things-to-do
  // on a single-page Brannan event) forces the classic Brannan render even
  // when brannanNavigation === singlePage — mirrors the switch in
  // GuestSiteLayout above — so treat it as "classic" here too.
  //
  // Computed here (before event/layout resolve) so `PageContentVisibilityProvider`
  // can mount at the very top of the return — `PrefetchSsrQueriesRenderer`
  // then fires layout-level queries in parallel with `EventWebsiteGuest`
  // instead of waiting behind the `event && layout` gate. Once `layout`
  // resolves, the provider's effect picks up the new `isInitiallyRevealed`
  // value and flips `allRevealed` accordingly.
  const isInitiallyRevealed = layout === DesignLayoutType.brannan && (isNativeBrannanOverridePage || event?.eventDesign?.brannanNavigation !== BrannanNavigation.singlePage);

  return (
    <PageContentVisibilityProvider
      eventHandle={eventHandle}
      pages={event?.pages}
      isInitiallyRevealed={isInitiallyRevealed}
    >
      <PrefetchSsrQueriesRenderer eventHandle={eventHandle} />
      <HoneymoonAccommodationsPrefetchRenderer
        eventHandle={eventHandle}
        eventId={event?.id}
      />
      {/* Only render GuestSiteHead when full SSR is enabled — when disabled,
          GuestSiteHeadCsr in GuestSite.tsx handles SEO meta tags */}
      {isFullySsrEnabled && <GuestSiteHead event={event} />}
      <Box paddingTop={totalOffset}>
        <LazyStatusBanner />
        <GuestSiteFeatureSet
          loadingGuestSite={loadingGuestSite || isLoadingWebsitePublishCapability}
          staticContext={staticContext}
        >
          {event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <EventDesignThemingProvider eventDesign={event.eventDesign}>
                <UnlockDialogProvider>
                  <AutoUnlockGate
                    hasPages={event.pages.length > 0}
                    hasUnlocked={event.activeSession.hasUnlocked}
                    isPreviewing={isPreviewing}
                  />
                  <GuestIdentificationDialogProvider>
                    <ColorPaletteProvider eventDesign={event.eventDesign}>
                      <EventDesignJoyKitThemeOverridesProvider>
                        <RichTextStylesProvider>
                          {showAnnouncement && !isNative && (
                            <LazyAnnouncement
                              onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                              content={<>{event.info.announcementBanner.message}</>}
                              title={event.info.announcementBanner.title}
                              graphicAccent={graphicAccent}
                            />
                          )}
                          {justFonts ? (
                            <JustFonts />
                          ) : (
                            <ScrollToProvider>
                              <AccommodationsLayoutOverrideProvider>
                                {isPreviewing && (
                                  <LazyPreviewListener
                                    eventHandle={eventHandle}
                                    eventPageRoutes={eventPageRoutes}
                                    eventDesign={event?.eventDesign}
                                    onRefetchData={handleOnRefetchData}
                                    onUpdate={handleOnPreviewUpdate}
                                  />
                                )}
                                <PremiumHeaderLayoutContext.Provider value={premiumHeaderLayout}>
                                  <StickyNavBarOffsetProvider offset={bannerOffset}>
                                    <div id={GUEST_SITE_START_PORTAL_ID} />
                                    <GuestSiteLayout
                                      setLoadingTheme={setLoadingTheme}
                                      eventHandle={eventHandle}
                                      event={event}
                                      layout={layout}
                                      setIsPastFirstPage={setIsPastFirstPage}
                                      isNativeAccommodationPage={isNativeAccommodationPage}
                                      isNativeBrannanOverridePage={isNativeBrannanOverridePage}
                                    />
                                  </StickyNavBarOffsetProvider>
                                </PremiumHeaderLayoutContext.Provider>
                              </AccommodationsLayoutOverrideProvider>
                            </ScrollToProvider>
                          )}
                          <HeaderLayoutUpgradeDialog
                            isOpen={premiumHeaderLayout.dialog.isOpen}
                            onClose={premiumHeaderLayout.dialog.onClose}
                            secondaryAction={premiumHeaderLayout.dialog.secondaryAction}
                            onDialogOpen={() => guestSiteTelemetry.headerLayoutUpgradeDialog.enter()}
                            onUpgradeClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogUpgradeClicked()}
                            onNoThanksClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogNoThanksClicked()}
                            onAllDoneClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogAllDoneClicked()}
                          />
                        </RichTextStylesProvider>
                      </EventDesignJoyKitThemeOverridesProvider>
                      <GuestSiteDebugger event={event} />
                    </ColorPaletteProvider>
                    {shouldRenderActionBar && <ActionBar eventId={event.id} />}
                  </GuestIdentificationDialogProvider>
                </UnlockDialogProvider>
              </EventDesignThemingProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      <SmartAppBanner telemetryCategory="wedding" />
      {/* Defer rendering the purchase confirmation dialog because it imports from registry app */}
      {shouldRenderPurchaseConfirmationDialog && (
        <LazyPurchaseConfirmationDialog
          isOpen={isPurchaseConfirmationDialogOpen}
          onClose={onPurchaseConfirmationClose}
        />
      )}
    </PageContentVisibilityProvider>
  );
});

const GuestSiteBody: React.FC<GuestSiteBodyProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <SmartAppBannerProvider>
          <WebsiteBannerProvider>
            <TemplatePreviewProvider>
              <GuestSiteBodyBase {...props} />
            </TemplatePreviewProvider>
          </WebsiteBannerProvider>
        </SmartAppBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSiteBody.displayName = 'GuestSiteBody';

export default GuestSiteBody;
